/**
 * Leaflet 地図 (フォームモーダル内 / 一覧地図セクションで共用)。
 *
 * 主な責務:
 *   1. 編集中の放送地点を Marker (ドラッグ可) + Circle (半径) で表示する。
 *   2. 同じ routeDirection の他の放送地点は Marker で重ねて表示し、位置関係を確認できる。
 *   3. 線形データ (Point の集合) を CircleMarker で描画する。
 *      - GeoJSON が存在しない場合は描画しない。
 *      - 右上のトグルにより、アナウンス有無 / GPS 精度 (Log スケール) の
 *        2 通りの色分けを切り替えられる。
 *   4. 一覧地図モード: 編集対象が無くても、`otherPoints` のピンを地図上に並べる。
 *      ピンを `onPointClick` でクリックすると親が編集モーダルを開ける。
 *   5. 地図上クリックでマーカー位置を移動し、フォーム側に座標を反映させる
 *      (`onLocationChange` が指定された場合のみ)。
 *   6. 右上の駅ジャンプドロップダウン: メンテナンス対象線区の駅一覧 (`stations`) から
 *      駅を選ぶと、その駅と前後の駅が収まる縮尺で地図をジャンプする。
 *   7. 路線の線形 (LineString, `routeLine`) を線区カラー (`routeLineColor`) の
 *      Polyline で描画する (例: 横浜線)。GPS 実測 Point・ピンより下のレイヤに置く。
 *   8. `fitToRoute` 指定時は、対象路線の全体 (駅 / 線形) が収まる縮尺で表示する。
 *   9. 駅 (`stations`) のうち緯度経度を持つものを、駅名ラベル付きのマーカーで表示する
 *      (Issue #676)。放送地点ピンより下のレイヤに置き、クリック操作は妨げない。
 *  10. 地図上の右クリックで独自のコンテキストメニュー「放送地点を新規作成」を表示する
 *      (`onRegisterAtLocation` が指定された場合のみ)。
 *      - メニュー項目の文言は `contextMenuLabel` で差し替えられる (未指定は「放送地点を新規作成」)。
 *        フォームモーダル内の地図では「この位置を放送地点の位置にする」として、
 *        編集中の地点の座標をセットする用途に使う。
 *      - 標準メニューを抑止するのは **地図コンテナ内だけ**。一覧テーブルなど地図の外や、
 *        右上のオーバーレイ (トグル / 駅ジャンプ) は標準のブラウザメニューのまま。
 *      - ピン (Marker) 上の右クリックではメニューを出さない (ピンの座標で誤登録しないため)。
 *      - メニュー自体は `MapContainer` の外側 (`.map-with-overlay` 直下) に描画する。
 */
import L from 'leaflet';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Circle,
  CircleMarker,
  MapContainer,
  Marker,
  Polyline,
  TileLayer,
  useMap,
  useMapEvents,
} from 'react-leaflet';

import type { BroadcastPoint } from '../api/types';
import {
  routeLineToLatLngs,
  type RouteLineFile,
  type RouteShapePointFeature,
  type RouteShapePointsFile,
} from '../data/routeShapes';
import {
  computeStationJumpLatLngs,
  filterStationsWithLatLon,
  type RouteStop,
} from '../data/routeStops';

// Leaflet のデフォルトアイコンは bundler 経由だとパスが解決できないため、CDN URL に差し替える。
// (react-leaflet 5 + leaflet 1.9 の組み合わせの定石)
const DEFAULT_ICON = L.icon({
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

const SECONDARY_ICON = L.divIcon({
  html: '<div style="width:14px;height:14px;border-radius:50%;background:#94a3b8;border:2px solid #fff;box-shadow:0 0 0 1px #94a3b8"></div>',
  className: '',
  iconSize: [14, 14],
  iconAnchor: [7, 7],
});

// 一覧表示時のピン (クリック可能・色を強調)
const LIST_ICON = L.divIcon({
  html: '<div style="width:18px;height:18px;border-radius:50%;background:#2563eb;border:3px solid #fff;box-shadow:0 0 0 1px #1d4ed8;cursor:pointer"></div>',
  className: '',
  iconSize: [18, 18],
  iconAnchor: [9, 9],
});

L.Marker.prototype.options.icon = DEFAULT_ICON;

/** HTML に埋め込む文字列をエスケープする (駅名を divIcon の html に入れるため) */
function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * 駅マーカーのアイコン (白地に濃色の縁取りの丸 + 駅名ラベル) を生成する。
 * 駅名は HTML エスケープして埋め込む。
 *
 * `export` しているのは、Leaflet/JSDOM 非依存でユニットテストできるようにするため。
 */
export function createStationIcon(stationName: string): L.DivIcon {
  return L.divIcon({
    html:
      '<div class="map-station-marker">' +
      '<span class="map-station-marker__dot"></span>' +
      `<span class="map-station-marker__label">${escapeHtml(stationName)}</span>` +
      '</div>',
    className: 'map-station-marker-icon',
    iconSize: [12, 12],
    iconAnchor: [6, 6],
  });
}

/** 線形カラーモード: アナウンス有無 / GPS 精度 (Log) */
export type RouteShapeColorMode = 'announce' | 'gps_accuracy';

export interface BroadcastPointsMapProps {
  /** 編集中の地点 (緯度経度・半径)。新規作成時は緯度経度未設定でも可 */
  editingLatitude?: number;
  editingLongitude?: number;
  editingRadiusMeters?: number;
  /** 同 routeDirection の他の放送地点 (グレー / 一覧モードでは青ピン) */
  otherPoints: BroadcastPoint[];
  /**
   * 線形データ (Point Feature の集合)。
   * `null` の場合は線形を描画しない (= GeoJSON 未配信のケース)。
   */
  routeShapePoints?: RouteShapePointsFile | null;
  /** マーカーがドラッグまたはクリックで移動された時に呼ばれる (フォーム用) */
  onLocationChange?: (lat: number, lng: number) => void;
  /**
   * `otherPoints` のピンをクリックした時に呼ばれる (一覧地図モード)。
   * 指定されている場合、ピンはクリック可能な強調表示で描画され、半径円も併せて描く。
   */
  onPointClick?: (point: BroadcastPoint) => void;
  /**
   * 地図をフォーカスしたい地点。この prop が変わると `flyTo` で地点に移動する。
   * 一覧テーブルのレコードをクリックした時に地図をジャンプさせるために使う。
   */
  focusPoint?: BroadcastPoint | null;
  /**
   * メンテナンス対象線区の駅一覧 (駅順マスタ)。
   * 緯度経度を持つ駅が 1 件以上あるとき、右上に駅ジャンプ用のドロップダウンを表示する。
   * 未指定 / 空配列 (全線区モード・取得失敗など) の場合は表示しない。
   */
  stations?: RouteStop[];
  /**
   * 路線の線形 (LineString)。指定されていれば `routeLineColor` で線を描く。
   * `null` / 未指定なら描画しない (線形データ未配置の線区)。
   */
  routeLine?: RouteLineFile | null;
  /** 路線線形の描画色 (`#RRGGBB`。routes-master.json の route_color)。未指定時はグレー */
  routeLineColor?: string;
  /**
   * `true` のとき、対象路線の **全体** (駅 / 路線線形 / 線形 Point) が収まる縮尺で表示する。
   * 新規作成時に地図を最前面で開いた瞬間、路線全体を見渡せるようにするために使う。
   * 線形などは非同期で届くため、届くたびに範囲を合わせ直す
   * (ただしユーザーが地図を操作した後は勝手に動かさない)。
   */
  fitToRoute?: boolean;
  /**
   * `fitToRoute` の合わせ直しを再開するキー。値が変わると「ユーザーが地図を操作した」状態を解除し、
   * あらためて路線全体に合わせる (線区 / 方面を切り替えたときに使う)。
   */
  fitResetKey?: string;
  /**
   * 地図を右クリック → メニュー項目 (既定「放送地点を新規作成」) を選んだ時に呼ばれる。
   * 緯度経度は小数 6 桁に丸めて渡す。指定されていなければ右クリックメニューは出さず、
   * 標準のブラウザメニューのままにする。
   * - 一覧地図: 新規作成フォームを開く。
   * - フォームモーダル内の地図: 編集中の地点の座標をセットする。
   */
  onRegisterAtLocation?: (lat: number, lng: number) => void;
  /**
   * 右クリックメニュー項目の文言。未指定なら「放送地点を新規作成」。
   * `onRegisterAtLocation` の意味に合わせて差し替える。
   */
  contextMenuLabel?: string;
  /**
   * 登録できない理由 (全線区モード・方面未選択など)。
   * 指定されている間はメニュー項目を無効 (グレーアウト) にし、理由を併記する。
   */
  registerDisabledReason?: string;
}

/** 路線全体の範囲 `[[南端 lat, 西端 lng], [北端 lat, 東端 lng]]` */
export type RouteBoundsLatLngs = [[number, number], [number, number]];

/**
 * 対象路線の **全体** が収まる範囲 (bounds) を算出するピュア関数。
 *
 * 新規作成時に地図を開いた瞬間、路線全体が見える縮尺で表示するために使う。
 * 範囲の算出には次の座標をすべて使う (いずれかが欠けていても残りで算出する)。
 *   - 駅順マスタの駅座標 (`stations`)
 *   - 路線線形 LineString (`routeLineLatLngs`)
 *   - 線形 Point Feature (`shapeFeatures`)
 *
 * 有限値の座標が 1 件も無い場合は `null` を返す (呼び出し側は従来の初期表示のまま)。
 */
export function computeRouteBounds(
  stations: RouteStop[],
  routeLineLatLngs: [number, number][][],
  shapeFeatures: RouteShapePointFeature[],
): RouteBoundsLatLngs | null {
  let minLat = Infinity;
  let minLng = Infinity;
  let maxLat = -Infinity;
  let maxLng = -Infinity;
  const add = (lat: number, lng: number): void => {
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
    if (lat < minLat) minLat = lat;
    if (lat > maxLat) maxLat = lat;
    if (lng < minLng) minLng = lng;
    if (lng > maxLng) maxLng = lng;
  };
  for (const s of stations) add(s.stop_lat as number, s.stop_lon as number);
  for (const line of routeLineLatLngs) for (const [lat, lng] of line) add(lat, lng);
  for (const f of shapeFeatures) {
    const [lng, lat] = f.geometry.coordinates;
    add(lat, lng);
  }
  if (minLat === Infinity) return null;
  return [
    [minLat, minLng],
    [maxLat, maxLng],
  ];
}

/** 路線線形の既定色 (route_color が取れない場合) */
const DEFAULT_ROUTE_LINE_COLOR = '#6b7280';

/** 右クリックメニューの概算サイズ (px)。端で見切れないよう位置を補正するために使う */
const CONTEXT_MENU_WIDTH = 180;
/** 文言を差し替えた (長めの) メニューの幅。表示位置の右端はみ出し判定に使う */
const CONTEXT_MENU_WIDTH_WIDE = 240;
/** 右クリックメニュー項目の既定文言 */
const DEFAULT_CONTEXT_MENU_LABEL = '放送地点を新規作成';
const CONTEXT_MENU_HEIGHT = 40;
/** 無効理由の行が加わった時の高さ */
const CONTEXT_MENU_HEIGHT_WITH_HINT = 84;

/** 緯度経度を小数 6 桁 (約 0.1m) に丸める。フォームの表示桁数と揃える */
export function roundCoordinate(value: number): number {
  return Math.round(value * 1e6) / 1e6;
}

/**
 * 右クリックメニューの表示位置を算出するピュア関数。
 *
 * クリック位置 (`x`, `y`: 地図コンテナ内の px) を基点とし、メニューが地図の右端 / 下端を
 * はみ出す場合は反対側 (クリック位置の左 / 上) に開く。それでも収まらない場合は 0 に丸める。
 */
export function computeContextMenuPosition(
  x: number,
  y: number,
  mapWidth: number,
  mapHeight: number,
  menuWidth: number = CONTEXT_MENU_WIDTH,
  menuHeight: number = CONTEXT_MENU_HEIGHT,
): { left: number; top: number } {
  const left = x + menuWidth > mapWidth ? x - menuWidth : x;
  const top = y + menuHeight > mapHeight ? y - menuHeight : y;
  return { left: Math.max(0, left), top: Math.max(0, top) };
}

/** 開いている右クリックメニューの状態 */
interface ContextMenuState {
  left: number;
  top: number;
  lat: number;
  lng: number;
}

/**
 * 駅ジャンプ要求。
 * - `latLngs`: 選択駅と前後の駅の座標 (選択時点で確定させ、駅一覧の再取得で再ジャンプしないようにする)
 * - `seq`: 同じ駅を再選択しても再ジャンプできるようにするための連番
 */
interface StationJumpRequest {
  latLngs: [number, number][];
  seq: number;
}

export function BroadcastPointsMap(props: BroadcastPointsMapProps): React.JSX.Element {
  const {
    editingLatitude,
    editingLongitude,
    editingRadiusMeters,
    otherPoints,
    routeShapePoints,
    onLocationChange,
    onPointClick,
    focusPoint,
    stations,
    routeLine,
    routeLineColor,
    fitToRoute,
    fitResetKey,
    onRegisterAtLocation,
    registerDisabledReason,
    contextMenuLabel,
  } = props;

  // 右クリックメニュー (地図上でのみ表示。null = 非表示)
  const [contextMenu, setContextMenu] = useState<ContextMenuState | null>(null);
  const closeContextMenu = useCallback((): void => setContextMenu(null), []);
  const menuHeight = registerDisabledReason ? CONTEXT_MENU_HEIGHT_WITH_HINT : CONTEXT_MENU_HEIGHT;
  const menuWidth = contextMenuLabel ? CONTEXT_MENU_WIDTH_WIDE : CONTEXT_MENU_WIDTH;

  // 路線線形を Leaflet の [lat, lng] 折れ線の配列に変換する (43 本程度なので軽い)。
  const routeLineLatLngs = useMemo(() => routeLineToLatLngs(routeLine), [routeLine]);

  // 緯度経度を持つ駅のみ (stop_sequence 昇順)。ドロップダウンの候補とジャンプ計算に使う。
  const jumpableStations = useMemo(() => filterStationsWithLatLon(stations), [stations]);
  const [selectedStopId, setSelectedStopId] = useState('');
  const [jumpRequest, setJumpRequest] = useState<StationJumpRequest | null>(null);
  // 線区が切り替わって選択中の駅が候補から消えた場合は、未選択として表示する。
  const shownStopId = jumpableStations.some((s) => s.stop_id === selectedStopId)
    ? selectedStopId
    : '';

  const handleStationSelect = (stopId: string): void => {
    setSelectedStopId(stopId);
    if (!stopId) return;
    const latLngs = computeStationJumpLatLngs(jumpableStations, stopId);
    if (latLngs.length === 0) return;
    setJumpRequest((prev) => ({ latLngs, seq: (prev?.seq ?? 0) + 1 }));
  };

  // 線形カラーモードのトグル状態。
  // `false` (= デフォルト) でアナウンス有無、`true` で GPS 精度 (Log)。
  const [gpsMode, setGpsMode] = useState(false);
  const colorMode: RouteShapeColorMode = gpsMode ? 'gps_accuracy' : 'announce';

  // 線形 Feature が利用可能か (= GeoJSON が存在するか)
  const shapeFeatures = useMemo<RouteShapePointFeature[]>(() => {
    if (!routeShapePoints || !Array.isArray(routeShapePoints.features)) return [];
    return routeShapePoints.features.filter(
      (f) => f.geometry?.type === 'Point' && Array.isArray(f.geometry.coordinates),
    );
  }, [routeShapePoints]);

  // 路線全体の範囲 (fitToRoute 時のみ算出。数万点の走査を無駄に行わないため)
  const routeBounds = useMemo(
    () =>
      fitToRoute ? computeRouteBounds(jumpableStations, routeLineLatLngs, shapeFeatures) : null,
    [fitToRoute, jumpableStations, routeLineLatLngs, shapeFeatures],
  );

  // 初期表示中心: 編集中の座標 → 既存地点の最初 → 線形の最初の点 → 東京駅
  const initialCenter: [number, number] = useMemo(() => {
    if (editingLatitude !== undefined && editingLongitude !== undefined) {
      return [editingLatitude, editingLongitude];
    }
    if (otherPoints.length > 0) {
      const first = otherPoints.find((p) => p.latitude && p.longitude);
      if (first?.latitude && first?.longitude) {
        return [first.latitude, first.longitude];
      }
    }
    if (shapeFeatures.length > 0) {
      const [lng, lat] = shapeFeatures[0]!.geometry.coordinates;
      return [lat, lng];
    }
    const firstLinePoint = routeLineLatLngs[0]?.[0];
    if (firstLinePoint) return firstLinePoint;
    return [35.681236, 139.767125];
  }, [editingLatitude, editingLongitude, otherPoints, shapeFeatures, routeLineLatLngs]);

  return (
    <div className="map-with-overlay">
      <MapContainer
        center={initialCenter}
        zoom={14}
        scrollWheelZoom
        style={{ width: '100%', height: '100%' }}
      >
        <TileLayer
          attribution="&copy; <a href='https://www.openstreetmap.org/copyright'>OpenStreetMap</a> contributors"
          url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {/* 路線の線形 (線区カラー)。最下層に置き、GPS 実測点やピンを隠さない */}
        {routeLineLatLngs.length > 0 ? (
          <RouteLineLayer
            lines={routeLineLatLngs}
            color={routeLineColor ?? DEFAULT_ROUTE_LINE_COLOR}
          />
        ) : null}
        {/* 駅 (緯度経度を持つもののみ)。放送地点ピンより下に置く */}
        {jumpableStations.length > 0 ? <StationMarkersLayer stations={jumpableStations} /> : null}
        {/* 線形データ (GeoJSON が無い時は描画しない) */}
        {shapeFeatures.length > 0 ? (
          <RouteShapePointsLayer features={shapeFeatures} colorMode={colorMode} />
        ) : null}
        {/* 既存の他の地点。一覧モード (onPointClick あり) ではクリック可能な青ピン + 半径円 */}
        {otherPoints
          .filter((p) => p.latitude !== undefined && p.longitude !== undefined)
          .map((p) => {
            if (onPointClick) {
              return (
                <PointWithCircle
                  key={`${p.routeDirection}-${p.spotId}`}
                  point={p}
                  onClick={() => onPointClick(p)}
                />
              );
            }
            return (
              <Marker
                key={`${p.routeDirection}-${p.spotId}`}
                position={[p.latitude as number, p.longitude as number]}
                icon={SECONDARY_ICON}
                interactive={false}
              />
            );
          })}
        {/* 編集中の地点 (青) */}
        {editingLatitude !== undefined && editingLongitude !== undefined ? (
          <>
            <Marker
              position={[editingLatitude, editingLongitude]}
              draggable={Boolean(onLocationChange)}
              eventHandlers={
                onLocationChange
                  ? {
                      dragend: (e) => {
                        const m = e.target as L.Marker;
                        const { lat, lng } = m.getLatLng();
                        onLocationChange(lat, lng);
                      },
                    }
                  : undefined
              }
            />
            {editingRadiusMeters && editingRadiusMeters > 0 ? (
              <Circle
                center={[editingLatitude, editingLongitude]}
                radius={editingRadiusMeters}
                pathOptions={{ color: '#1d4ed8', fillColor: '#1d4ed8', fillOpacity: 0.1 }}
              />
            ) : null}
            <RecenterOnChange lat={editingLatitude} lng={editingLongitude} />
          </>
        ) : null}
        {onLocationChange ? <ClickHandler onLocationChange={onLocationChange} /> : null}
        {onRegisterAtLocation ? (
          <ContextMenuHandler
            menuWidth={menuWidth}
            menuHeight={menuHeight}
            onOpen={setContextMenu}
            onClose={closeContextMenu}
          />
        ) : null}
        {focusPoint ? <FlyToPoint point={focusPoint} /> : null}
        {jumpRequest ? <FlyToStation request={jumpRequest} /> : null}
        {routeBounds ? <FitToRouteBounds bounds={routeBounds} resetKey={fitResetKey} /> : null}
        <InvalidateOnResize />
      </MapContainer>
      {/* 右上のオーバーレイ (線形カラーモード → 駅ジャンプの順) を縦に積む */}
      {jumpableStations.length > 0 || shapeFeatures.length > 0 ? (
        <div className="map-overlay-stack">
          {/* トグル + 凡例 (線形 GeoJSON が無い時は非表示) */}
          {shapeFeatures.length > 0 ? (
            <ColorModeToggle gpsMode={gpsMode} onChange={setGpsMode} />
          ) : null}
          {/* 駅ジャンプは「アナウンス有無 / GPS精度」トグルの下に置く */}
          {jumpableStations.length > 0 ? (
            <StationJumpSelect
              stations={jumpableStations}
              value={shownStopId}
              onChange={handleStationSelect}
            />
          ) : null}
        </div>
      ) : null}
      {/* 右クリックメニュー。MapContainer の外側に描くため、メニュー上の右クリックは標準メニューになる */}
      {onRegisterAtLocation && contextMenu ? (
        <MapContextMenu
          left={contextMenu.left}
          top={contextMenu.top}
          label={contextMenuLabel}
          disabledReason={registerDisabledReason}
          onSelect={() => {
            onRegisterAtLocation(contextMenu.lat, contextMenu.lng);
            closeContextMenu();
          }}
          onClose={closeContextMenu}
        />
      ) : null}
    </div>
  );
}

/**
 * 地図コンテナのサイズ変化を検知して Leaflet の `invalidateSize` を呼ぶ。
 *
 * モーダル内でフォーム側のコンテンツ数が増えるなどして親要素の高さが
 * 動的に変わった際、Leaflet はマウント時のサイズをキャッシュしているため
 * タイルが見切れたり灰色領域が出たりする。`ResizeObserver` で監視し、
 * サイズ変化のたびに `invalidateSize` を呼んで再描画させる。
 */
function InvalidateOnResize(): null {
  const map = useMap();
  useEffect(() => {
    const container = map.getContainer();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => {
      map.invalidateSize();
    });
    ro.observe(container);
    return () => {
      ro.disconnect();
    };
  }, [map]);
  return null;
}

/** 一覧表示用: 1 つの BroadcastPoint をクリック可能なピン + 半径円で描画する */
function PointWithCircle({
  point,
  onClick,
}: {
  point: BroadcastPoint;
  onClick: () => void;
}): React.JSX.Element | null {
  if (point.latitude === undefined || point.longitude === undefined) return null;
  const radius = point.radius_meters && point.radius_meters > 0 ? point.radius_meters : 100;
  return (
    <>
      <Circle
        center={[point.latitude, point.longitude]}
        radius={radius}
        pathOptions={{
          color: '#94a3b8',
          weight: 1,
          fillColor: '#94a3b8',
          fillOpacity: 0.08,
        }}
      />
      <Marker
        position={[point.latitude, point.longitude]}
        icon={LIST_ICON}
        eventHandlers={{ click: onClick }}
        title={point.description ?? point.routeDirection}
      />
    </>
  );
}

/** 地図クリックで onLocationChange を呼ぶハンドラ */
function ClickHandler({
  onLocationChange,
}: {
  onLocationChange: (lat: number, lng: number) => void;
}): null {
  useMapEvents({
    click(e) {
      onLocationChange(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
}

/**
 * 地図の右クリック (`contextmenu`) を捕まえて、メニューの表示位置と対象座標を親へ通知する。
 *
 * - Leaflet は `contextmenu` の購読者がいるとき、**地図コンテナ内でのみ** 標準メニューを抑止する。
 *   地図の外 (一覧テーブルなど) には影響しない。
 * - 座標は `latlng` ではなくマウス位置から求める。半径円 (Circle) 上では `latlng` が
 *   円の中心になることがあり、意図した位置に登録できなくなるため。
 * - 地図のクリック / 移動 / ズームが始まったらメニューを閉じる。
 */
function ContextMenuHandler({
  menuWidth,
  menuHeight,
  onOpen,
  onClose,
}: {
  menuWidth: number;
  menuHeight: number;
  onOpen: (state: ContextMenuState) => void;
  onClose: () => void;
}): null {
  const map = useMapEvents({
    contextmenu(e) {
      const original = e.originalEvent;
      // ピン上ではメニューを出さない (ピン自身は contextmenu を購読しないため地図まで届く)。
      // Leaflet は購読者がいるので標準メニューも抑止済みであり、何も出さないのが意図した挙動。
      onClose();
      if ((original.target as HTMLElement | null)?.closest?.('.leaflet-marker-icon')) return;
      const point = map.mouseEventToContainerPoint(original);
      const latlng = map.mouseEventToLatLng(original).wrap();
      const size = map.getSize();
      const { left, top } = computeContextMenuPosition(
        point.x,
        point.y,
        size.x,
        size.y,
        menuWidth,
        menuHeight,
      );
      onOpen({
        left,
        top,
        lat: roundCoordinate(latlng.lat),
        lng: roundCoordinate(latlng.lng),
      });
    },
    click: onClose,
    movestart: onClose,
    zoomstart: onClose,
  });
  return null;
}

/**
 * 地図上の右クリックメニュー (「放送地点を新規作成」1 項目)。
 *
 * - Esc キー / メニュー外の押下で `onClose` を呼ぶ。
 * - `disabledReason` があるときは項目を無効にして理由を表示する。
 * - メニュー内のマウス操作は地図へ伝播させない (ドラッグ / クリックの誤爆防止)。
 *
 * `export` しているのは、Leaflet/JSDOM 非依存でユニットテストできるようにするため。
 */
export function MapContextMenu({
  left,
  top,
  label = DEFAULT_CONTEXT_MENU_LABEL,
  disabledReason,
  onSelect,
  onClose,
}: {
  left: number;
  top: number;
  label?: string;
  disabledReason?: string;
  onSelect: () => void;
  onClose: () => void;
}): React.JSX.Element {
  const menuRef = useRef<HTMLDivElement>(null);
  const disabled = Boolean(disabledReason);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        // 地図をポップアップで開いている場合 (フォーム内の地図) に、Esc でメニューを閉じた
        // ついでに地図まで閉じてしまわないよう、window への伝播を止める。
        e.stopPropagation();
        onClose();
      }
    };
    const onPointerDown = (e: PointerEvent): void => {
      if (menuRef.current && e.target instanceof Node && menuRef.current.contains(e.target)) {
        return;
      }
      onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('pointerdown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('pointerdown', onPointerDown);
    };
  }, [onClose]);

  const stop: React.MouseEventHandler = (e) => {
    e.stopPropagation();
  };

  return (
    <div
      ref={menuRef}
      className="map-context-menu"
      role="menu"
      data-testid="map-context-menu"
      style={{ left, top }}
      onMouseDown={stop}
      onDoubleClick={stop}
      onWheel={stop}
      onClick={stop}
    >
      <button
        type="button"
        role="menuitem"
        className="map-context-menu__item"
        data-testid="map-context-menu-register"
        disabled={disabled}
        title={disabledReason}
        onClick={onSelect}
      >
        {label}
      </button>
      {disabledReason ? (
        <div className="map-context-menu__hint" data-testid="map-context-menu-hint">
          {disabledReason}
        </div>
      ) : null}
    </div>
  );
}

/** 編集中の緯度経度が変わった時、地図中心を緩やかに移動する */
function RecenterOnChange({ lat, lng }: { lat: number; lng: number }): null {
  const map = useMap();
  useEffect(() => {
    map.panTo([lat, lng]);
  }, [map, lat, lng]);
  return null;
}

/**
 * `point` が変わるたびに地図をその緯度経度へ `flyTo` するコンポーネント。
 * 座標が未設定の地点は何もしない。
 */
function FlyToPoint({ point }: { point: BroadcastPoint }): null {
  const map = useMap();
  useEffect(() => {
    if (typeof point.latitude === 'number' && typeof point.longitude === 'number') {
      map.flyTo([point.latitude, point.longitude], map.getZoom(), { duration: 0.5 });
    }
  }, [map, point]);
  return null;
}

/** 路線全体に合わせる際の余白 (px)。路線の端が地図の縁に貼り付かないようにする */
const ROUTE_FIT_PADDING: [number, number] = [24, 24];

/**
 * 対象路線の全体が収まる縮尺に地図を合わせる (`fitToRoute`)。
 *
 * - 線形 / 駅一覧は非同期で届き、範囲 (`bounds`) が広がっていくため、変わるたびに合わせ直す。
 * - 地図ポップアップは開いた直後にサイズが確定する (初期は 0 サイズ) ため、
 *   Leaflet の `resize` でも合わせ直す。
 * - ただしユーザーが地図を操作 (クリック / ドラッグ / ホイール) した後は、
 *   見ている位置を勝手に動かさないよう以後は何もしない。
 */
function FitToRouteBounds({
  bounds,
  resetKey,
}: {
  bounds: RouteBoundsLatLngs;
  resetKey?: string;
}): null {
  const map = useMap();
  const userInteractedRef = useRef(false);
  const lastResetKeyRef = useRef(resetKey);

  // ユーザー操作の検知 (fitBounds 自身も movestart / zoomstart を発火するため DOM イベントで判定する)
  useEffect(() => {
    const container = map.getContainer();
    const markInteracted = (): void => {
      userInteractedRef.current = true;
    };
    container.addEventListener('pointerdown', markInteracted);
    container.addEventListener('wheel', markInteracted);
    return () => {
      container.removeEventListener('pointerdown', markInteracted);
      container.removeEventListener('wheel', markInteracted);
    };
  }, [map]);

  useEffect(() => {
    // 線区 / 方面が切り替わったら、操作済みの状態を解除してあらためて路線全体に合わせる
    if (lastResetKeyRef.current !== resetKey) {
      lastResetKeyRef.current = resetKey;
      userInteractedRef.current = false;
    }
    const fit = (): void => {
      if (userInteractedRef.current) return;
      const size = map.getSize();
      if (size.x <= 0 || size.y <= 0) return;
      map.fitBounds(L.latLngBounds(bounds), { padding: ROUTE_FIT_PADDING, animate: false });
    };
    fit();
    map.on('resize', fit);
    return () => {
      map.off('resize', fit);
    };
  }, [map, bounds, resetKey]);
  return null;
}

// ---------------------------------------------------------------------------
// 駅ジャンプ関連
// ---------------------------------------------------------------------------

/** 駅が 1 つしか無い (前後駅が無い) 時のズームレベル */
const SINGLE_STATION_ZOOM = 15;
/** fitBounds 時の余白 (px)。前後の駅のピンが地図の縁に貼り付かないようにする */
const STATION_FIT_PADDING: [number, number] = [40, 40];

/** 駅ジャンプ要求を受けて、選択駅と前後の駅が収まる縮尺で地図を移動する */
function FlyToStation({ request }: { request: StationJumpRequest }): null {
  const map = useMap();
  useEffect(() => {
    const { latLngs } = request;
    if (latLngs.length === 0) return;
    if (latLngs.length === 1) {
      map.flyTo(latLngs[0]!, SINGLE_STATION_ZOOM, { duration: 0.5 });
      return;
    }
    map.flyToBounds(L.latLngBounds(latLngs), { padding: STATION_FIT_PADDING, duration: 0.5 });
    // request は選択のたびに新しいオブジェクトになるため、同じ駅の再選択でもジャンプする
  }, [map, request]);
  return null;
}

/**
 * 駅を駅名ラベル付きマーカーで描画する (Issue #676)。
 *
 * 放送地点のピン (通常の z-index) より下に出すため `zIndexOffset` を負にし、
 * 地図クリックによる位置指定を妨げないよう `interactive={false}` にする。
 */
function StationMarkersLayer({ stations }: { stations: RouteStop[] }): React.JSX.Element {
  return (
    <>
      {stations.map((s) => (
        <Marker
          key={`station-${s.stop_id}`}
          position={[s.stop_lat as number, s.stop_lon as number]}
          icon={createStationIcon(s.stop_name)}
          zIndexOffset={-1000}
          interactive={false}
        />
      ))}
    </>
  );
}

/**
 * 地図右上の駅ジャンプ用ドロップダウン。
 *
 * Leaflet の Map 上ではクリック等が地図イベントに伝播するため stopPropagation する
 * (`ColorModeToggle` と同様)。
 *
 * `export` しているのは、Leaflet/JSDOM 非依存でユニットテストできるようにするため。
 */
export function StationJumpSelect({
  stations,
  value,
  onChange,
}: {
  stations: RouteStop[];
  value: string;
  onChange: (stopId: string) => void;
}): React.JSX.Element {
  const stop: React.UIEventHandler = (e) => {
    e.stopPropagation();
  };
  return (
    <div
      className="map-station-jump"
      onMouseDown={stop}
      onDoubleClick={stop}
      onWheel={stop}
      onClick={stop}
    >
      <label className="map-station-jump__label">
        駅へ移動
        <select
          className="map-station-jump__select"
          aria-label="駅へ移動"
          value={value}
          onChange={(e) => onChange(e.target.value)}
        >
          <option value="">駅を選択…</option>
          {stations.map((s) => (
            <option key={s.stop_id} value={s.stop_id}>
              {s.stop_name}
            </option>
          ))}
        </select>
      </label>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 路線線形 (LineString) 描画
// ---------------------------------------------------------------------------

/**
 * 路線の線形を線区カラーで描画する。
 *
 * 横浜線の黄緑 (#8FBC1F) のような明るい色は OSM タイル上で埋もれやすいため、
 * 下に白い縁取り線を敷いてから線区カラーの線を重ねる (路線図でよく使う表現)。
 * どちらも `interactive={false}` にして、地図クリックによる位置指定を妨げない。
 */
function RouteLineLayer({
  lines,
  color,
}: {
  lines: [number, number][][];
  color: string;
}): React.JSX.Element {
  return (
    <>
      <Polyline
        positions={lines}
        pathOptions={{ color: '#ffffff', weight: 9, opacity: 0.9, lineCap: 'round' }}
        interactive={false}
      />
      <Polyline
        positions={lines}
        pathOptions={{ color, weight: 5, opacity: 0.95, lineCap: 'round' }}
        interactive={false}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// 線形 (Point Feature コレクション) 描画関連
// ---------------------------------------------------------------------------

/** GPS 精度 (m) の Log カラーマップ閾値。値が小さいほど高精度=水色、大きいほど赤。 */
const GPS_ACCURACY_MIN_METERS = 5;
const GPS_ACCURACY_MAX_METERS = 100;

/** GPS 精度 高精度側 (水色) */
const GPS_COLOR_HIGH: RgbColor = { r: 125, g: 211, b: 252 }; // tailwind sky-300 相当
/** GPS 精度 中央 (白) */
const GPS_COLOR_MID: RgbColor = { r: 255, g: 255, b: 255 };
/** GPS 精度 低精度側 (赤) */
const GPS_COLOR_LOW: RgbColor = { r: 220, g: 38, b: 38 }; // tailwind red-600 相当

/** アナウンス区間の色 (赤系) */
const COLOR_ANNOUNCE = '#dc2626';
/** 非アナウンス区間の色 (灰系) */
const COLOR_NO_ANNOUNCE = '#94a3b8';
/** 値が無い (null) 時の色 (薄灰) */
const COLOR_UNKNOWN = '#d1d5db';

/** RGB 0-255 の単純な値オブジェクト (HSL 補間だと中央が白にならないため、RGB 線形補間で扱う) */
interface RgbColor {
  r: number;
  g: number;
  b: number;
}

/**
 * 線形データを CircleMarker の集合として描画する。
 *
 * - `colorMode === 'announce'`: `is_announcing` (true/false/null) に応じて色分け
 * - `colorMode === 'gps_accuracy'`: `gps_accuracy_avg` を Log スケールで赤→青に色分け
 *
 * 大量 (数万点) の Point があるため、各点は半径 3px の小さな CircleMarker
 * (= 画面ピクセル基準) で描画して描画コストを抑える。
 */
function RouteShapePointsLayer({
  features,
  colorMode,
}: {
  features: RouteShapePointFeature[];
  colorMode: RouteShapeColorMode;
}): React.JSX.Element {
  return (
    <>
      {features.map((f, i) => {
        const [lng, lat] = f.geometry.coordinates;
        const color = computeShapeColor(f.properties, colorMode);
        return (
          <CircleMarker
            key={`shape-${i}`}
            center={[lat, lng]}
            radius={3}
            pathOptions={{
              color,
              fillColor: color,
              fillOpacity: 0.85,
              weight: 1,
              opacity: 0.9,
            }}
            interactive={false}
          />
        );
      })}
    </>
  );
}

/** 1 点の properties から表示色を決定する */
function computeShapeColor(
  props: RouteShapePointFeature['properties'],
  mode: RouteShapeColorMode,
): string {
  if (mode === 'announce') {
    if (props.is_announcing === true) return COLOR_ANNOUNCE;
    if (props.is_announcing === false) return COLOR_NO_ANNOUNCE;
    return COLOR_UNKNOWN;
  }
  // GPS 精度モード
  const acc = props.gps_accuracy_avg;
  if (acc === null || acc === undefined || !Number.isFinite(acc) || acc <= 0) {
    return COLOR_UNKNOWN;
  }
  return gpsAccuracyToColor(acc);
}

/**
 * GPS 精度 (m) を Log スケールに正規化し、`水色 → 白 → 赤` に補間する。
 *
 * - acc が `GPS_ACCURACY_MIN_METERS` 以下 → 水色
 * - acc が `GPS_ACCURACY_MIN_METERS` と `GPS_ACCURACY_MAX_METERS` の幾何平均 → 白
 * - acc が `GPS_ACCURACY_MAX_METERS` 以上 → 赤
 *
 * HSL 補間では中央が白色にならないため、RGB 各成分を線形補間する。
 */
export function gpsAccuracyToColor(acc: number): string {
  const t = gpsAccuracyToScalar(acc);
  return rgbToHex(twoStopGradient(GPS_COLOR_HIGH, GPS_COLOR_MID, GPS_COLOR_LOW, t));
}

/**
 * GPS 精度 (m) を [0, 1] の Log スケール正規化値に変換する。
 * 0 = 高精度 (水色側) / 1 = 低精度 (赤側)。
 */
export function gpsAccuracyToScalar(acc: number): number {
  const minLog = Math.log10(GPS_ACCURACY_MIN_METERS);
  const maxLog = Math.log10(GPS_ACCURACY_MAX_METERS);
  const v = Math.log10(Math.max(acc, 1e-6));
  return Math.min(1, Math.max(0, (v - minLog) / (maxLog - minLog)));
}

/**
 * 3 色グラデーションを `t in [0, 1]` で評価して RGB を返す。
 * - `t = 0` → `lo`
 * - `t = 0.5` → `mid`
 * - `t = 1` → `hi`
 */
function twoStopGradient(lo: RgbColor, mid: RgbColor, hi: RgbColor, t: number): RgbColor {
  if (t <= 0.5) {
    const u = t / 0.5;
    return lerpRgb(lo, mid, u);
  }
  const u = (t - 0.5) / 0.5;
  return lerpRgb(mid, hi, u);
}

function lerpRgb(a: RgbColor, b: RgbColor, t: number): RgbColor {
  return {
    r: Math.round(a.r + (b.r - a.r) * t),
    g: Math.round(a.g + (b.g - a.g) * t),
    b: Math.round(a.b + (b.b - a.b) * t),
  };
}

function rgbToHex({ r, g, b }: RgbColor): string {
  const toHex = (x: number): string => {
    const v = Math.max(0, Math.min(255, Math.round(x)));
    return v.toString(16).padStart(2, '0');
  };
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

/**
 * 地図右上のカラーモード切替コントロール + 凡例。
 *
 * Issue #130: 従来は ON/OFF の 1 つのトグルスイッチで、ラベル文言が状態に応じて
 * 「アナウンス有無」⇔「GPS精度 (Log)」と切り替わる方式だった。これは「今どちらの
 * モードか」「押すとどちらになるか」が分かりづらく使いづらかった。
 * そこで、2 つのモードを常に並べて表示し、現在選択中のモードを強調するセグメント
 * コントロール (ラジオ相当) に変更し、直感的に切り替えられるようにした。
 *
 * Leaflet の Map 内では DOM のクリックが地図イベントに伝播してしまうことが
 * あるため、`onClick` / `onMouseDown` などのイベントを stopPropagation する。
 *
 * `export` しているのは、Leaflet/JSDOM 非依存でユニットテストできるようにするため。
 */
export function ColorModeToggle({
  gpsMode,
  onChange,
}: {
  gpsMode: boolean;
  onChange: (next: boolean) => void;
}): React.JSX.Element {
  const stop: React.MouseEventHandler = (e) => {
    e.stopPropagation();
  };
  return (
    <div
      className="map-color-toggle"
      onMouseDown={stop}
      onDoubleClick={stop}
      onWheel={stop}
      onClick={stop}
    >
      <div
        className="map-color-toggle__segmented"
        role="radiogroup"
        aria-label="線形カラーモード切替"
      >
        <button
          type="button"
          role="radio"
          aria-checked={!gpsMode}
          className={`map-color-toggle__segment${!gpsMode ? ' is-active' : ''}`}
          onClick={() => onChange(false)}
        >
          アナウンス有無
        </button>
        <button
          type="button"
          role="radio"
          aria-checked={gpsMode}
          className={`map-color-toggle__segment${gpsMode ? ' is-active' : ''}`}
          onClick={() => onChange(true)}
        >
          GPS精度 (Log)
        </button>
      </div>
      <ColorModeLegend gpsMode={gpsMode} />
    </div>
  );
}

/** カラーモードに応じた凡例 (色サンプル) */
function ColorModeLegend({ gpsMode }: { gpsMode: boolean }): React.JSX.Element {
  if (!gpsMode) {
    return (
      <ul className="map-color-toggle__legend">
        <li>
          <span className="legend-swatch" style={{ background: COLOR_ANNOUNCE }} />
          アナウンス有
        </li>
        <li>
          <span className="legend-swatch" style={{ background: COLOR_NO_ANNOUNCE }} />
          アナウンス無
        </li>
        <li>
          <span className="legend-swatch" style={{ background: COLOR_UNKNOWN }} />
          未取得
        </li>
      </ul>
    );
  }
  // GPS 精度モード: 連続グラデーション + 両端の値を併記
  // (geom-mean は閾値が幾何平均=白になるよう Log スケールの中央)
  const midAcc = Math.sqrt(GPS_ACCURACY_MIN_METERS * GPS_ACCURACY_MAX_METERS);
  const gradient =
    `linear-gradient(to right,` +
    ` ${gpsAccuracyToColor(GPS_ACCURACY_MIN_METERS)} 0%,` +
    ` ${gpsAccuracyToColor(midAcc)} 50%,` +
    ` ${gpsAccuracyToColor(GPS_ACCURACY_MAX_METERS)} 100%)`;
  return (
    <div className="map-color-toggle__gradient">
      <span className="legend-label">高精度</span>
      <span className="legend-bar" style={{ background: gradient }} />
      <span className="legend-label">低精度</span>
      <div className="legend-range">
        {GPS_ACCURACY_MIN_METERS}m – {GPS_ACCURACY_MAX_METERS}m
      </div>
    </div>
  );
}
